import {
  CdkOverlayOrigin,
  ConnectedPosition,
  Overlay,
  OverlayModule,
} from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  takeUntil,
  throwError,
} from 'rxjs';
import { LookupOption, LookupTypeCodes } from '../../models/lookup.models';
import {
  AssignedResourceDto,
  PagedResult,
  RequestDto,
  RequestExportLayout,
  RequestListRequest,
} from '../../models/request.models';
import { PracticeAreaDto } from '../../models/resource.models';
import { PracticeArea } from '../../models/user.models';
import { AuthService } from '../../services/auth.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { LookupService } from '../../services/lookup.service';
import {
  ClientMatterInfoDto,
  MatterService,
} from '../../services/matter.service';
import { PracticeAreaService } from '../../services/practice-area.service';
import { RequestService } from '../../services/request.service';
import { ResourceService } from '../../services/resource.service';
import { SnackbarService } from '../../services/snackbar.service';
import { PageKey, UserCookieService } from '../../services/user-cookie.service';
import {
  downloadBlob,
  parseFilenameFromContentDisposition,
} from '../../utils/file-download.utils';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { ErrorStateComponent } from '../shared/error-state/error-state.component';
import {
  ExportConfigDialogComponent,
  ExportConfigDialogData,
  ExportConfigDialogResult,
} from '../shared/export-config-dialog/export-config-dialog.component';
import { MatterCellComponent } from '../shared/matter-cell/matter-cell.component';
import {
  MattersListDialogComponent,
  MattersListDialogData,
} from '../shared/matters-list-dialog/matters-list-dialog.component';
import { MembershipChipsComponent } from '../shared/membership-chips/membership-chips.component';
import { RequestDetailsComponent } from './request-details.component';
import { RequestWizardDialogComponent } from './request-wizard/request-wizard-dialog.component';

/**
 * One matter's worth of assigned resources for the Assigned-column popover.
 * `clientMatterNumber` is null for the trailing catch-all group holding resources
 * whose assignment matters no longer match any matter on the request.
 */
interface AssignedMatterGroup {
  clientMatterNumber: string | null;
  clientName?: string;
  matterName?: string;
  resources: AssignedResourceDto[];
}

@Component({
  selector: 'app-requests',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    OverlayModule,
    RouterModule,
    ErrorStateComponent,
    BreadcrumbComponent,
    MembershipChipsComponent,
    MatterCellComponent,
  ],
  templateUrl: './requests.component.html',
  styleUrls: ['./requests.component.scss'],
})
export class RequestsComponent implements OnInit, AfterViewInit, OnDestroy {
  private requestService = inject(RequestService);
  private lookupService = inject(LookupService);
  private matterService = inject(MatterService);
  private practiceAreaService = inject(PracticeAreaService);
  private resourceService = inject(ResourceService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);
  private userCookieService = inject(UserCookieService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);
  private overlay = inject(Overlay);

  readonly paginator = viewChild(MatPaginator);

  readonly sort = viewChild<MatSort>('headerSort');

  // Data properties
  requests: RequestDto[] = [];
  isLoading = false;
  isSearching = false;
  error: string | null = null;
  private sortSubscription: Subscription | null = null;

  assignedValues: Map<number, string> = new Map();

  // Assigned-column popover: the trigger the overlay is anchored to (null when closed)
  // and the matter-grouped resources it renders, rebuilt each time it is opened.
  assignedPopoverOrigin: CdkOverlayOrigin | null = null;
  assignedPopoverGroups: AssignedMatterGroup[] = [];
  /** Held while open so the groups can be rebuilt when matter names arrive mid-view. */
  private assignedPopoverRequest: RequestDto | null = null;
  /** Matter headings are only worth showing when the request spans more than one matter. */
  assignedPopoverShowHeadings = false;

  // Close on scroll rather than reposition: once the anchor cell moves under the sticky
  // header or out of the horizontal scroll window there is no sensible place to put the
  // popover. Safe to share one instance — OverlayRef.dispose() detaches the strategy.
  readonly assignedPopoverScrollStrategy = this.overlay.scrollStrategies.close();

  // Prefer opening below the cell; fall back above, then end-aligned for the columns
  // nearest the right edge of the table.
  readonly assignedPopoverPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  // Preserve sort state across filter changes
  currentSortActive: string = 'dateSubmitted';
  currentSortDirection: 'asc' | 'desc' = 'desc';

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;

  // Table configuration. `actions` is appended dynamically when the
  // `comparison-pop-ups` feature flag is enabled (see ngOnInit).
  displayedColumns: string[] = [
    'id',
    'dateSubmitted',
    'description',
    'clientMatter',
    'requestorName',
    'requestType',
    'practiceArea',
    'assigned',
    'status',
  ];

  private readonly COLUMN_MIN_WIDTHS: { [key: string]: number } = {
    id: 70,
    dateSubmitted: 130,
    description: 200,
    clientMatter: 150,
    requestorName: 150,
    requestType: 130,
    practiceArea: 160,
    assigned: 110,
    status: 120,
    actions: 96, // two icon buttons (view + open-in-window)
  };

  get tableMinWidth(): number {
    return this.displayedColumns.reduce(
      (total, column) => total + (this.COLUMN_MIN_WIDTHS[column] ?? 120),
      0,
    );
  }

  syncHeaderScroll(event: Event, header: HTMLElement): void {
    header.scrollLeft = (event.target as HTMLElement).scrollLeft;
  }

  // Filter form
  filterForm: FormGroup;
  searchControl: FormControl<string | null>;

  // Available filter options (loaded from lookup tables)
  availableStatuses: LookupOption[] = [];
  availableRequestTypes: LookupOption[] = [];
  // All practice areas, used to resolve codes to descriptions for the table badges. This must
  // cover every code that can appear on a request (including practice areas the user doesn't
  // belong to, e.g. on multi-practice-area requests), so it is sourced from the full list.
  practiceAreas: PracticeArea[] = [];
  // The logged-in user's practice areas, used to populate the filter dropdown so a user can
  // only filter by areas they have access to (the server enforces this independently).
  // Fetched fresh per load (like the resource directory) so newly granted areas appear without
  // a full page refresh.
  filterPracticeAreas: PracticeAreaDto[] = [];

  // Selected status filters
  selectedStatuses: string[] = ['New', 'Pending'];

  canManageRequests = false;

  /** True while an export download is in flight — dims the header Export action. */
  isExporting = false;

  // Remembered export layout — seeds the export-config dialog and is updated
  // whenever the user runs an export from it.
  lastExportType: RequestExportLayout = 'grouped';

  /** True while client/matter names are being loaded in the background for the current page. */
  loadingClientMatterDetails = false;

  /**
   * Hydrated client/matter info used to populate the matters dialog and the Assigned popover
   * headings. Keyed by upper-cased client-matter number: the matter API echoes back its own
   * casing, which is not guaranteed to match the numbers stored on the request.
   */
  private matterInfoByNumber = new Map<string, ClientMatterInfoDto>();

  private destroy$ = new Subject<void>();
  private pendingSearchRequest: Subscription | null = null;
  private pendingClientMatterInfoRequest: Subscription | null = null;

  constructor() {
    this.searchControl = new FormControl<string | null>('');
    this.filterForm = this.fb.group({
      requestTypes: [[]],
      practiceAreaCodes: [[]],
    });

    effect(() => {
      const sort = this.sort();
      if (sort) {
        this.setupSortSubscription(sort);
      }
    });
  }

  ngOnInit(): void {
    this.canManageRequests = this.authService.canCreateRequests();
    this.restoreSavedFilters();
    this.restoreSavedExportType();
    this.loadLookupOptions();
    this.loadPracticeAreas();
    this.setupSearchSubscription();
    this.loadRequests(true);

    // Toggle the actions column on the comparison-pop-ups flag.
    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        const hasColumn = this.displayedColumns.includes('actions');
        if (enabled && !hasColumn) {
          this.displayedColumns = [...this.displayedColumns, 'actions'];
        } else if (!enabled && hasColumn) {
          this.displayedColumns = this.displayedColumns.filter(
            (c) => c !== 'actions',
          );
        }
      });
  }

  navigateToRequest(request: RequestDto, event: MouseEvent): void {
    event.stopPropagation();
    if (request.id < 0) {
      return;
    }
    this.router.navigate(['/requests', request.id]);
  }

  openInFloatingDialog(request: RequestDto, event: MouseEvent): void {
    event.stopPropagation();
    if (request.id < 0) {
      return;
    }
    this.floatingDialogService.open(
      RequestDetailsComponent,
      `Request ${request.id}`,
      { requestId: request.id },
    );
  }

  ngAfterViewInit(): void {
    // Sort subscription is now handled by the setter
  }

  private setupSortSubscription(sort: MatSort): void {
    // Unsubscribe from previous sort subscription if it exists
    if (this.sortSubscription) {
      this.sortSubscription.unsubscribe();
    }

    // Restore the preserved sort state
    sort.active = this.currentSortActive;
    sort.direction = this.currentSortDirection;

    this.sortSubscription = sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Save the current sort state (only if direction is valid)
        if (sort.direction) {
          this.currentSortActive = sort.active;
          this.currentSortDirection = sort.direction;
        }

        this.pageIndex = 0; // Reset to first page on sort
        this.loadRequests();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private restoreSavedFilters(): void {
    const savedStatuses = this.userCookieService.getFilterSelection(
      PageKey.Requests,
      'statuses',
    );
    if (savedStatuses) {
      this.selectedStatuses = savedStatuses;
    }

    const savedRequestTypes = this.userCookieService.getFilterSelection(
      PageKey.Requests,
      'requestTypes',
    );
    const savedPracticeAreas = this.userCookieService.getFilterSelection(
      PageKey.Requests,
      'practiceAreaCodes',
    );
    const patch: { [key: string]: any } = {};
    if (savedRequestTypes) patch['requestTypes'] = savedRequestTypes;
    if (savedPracticeAreas) patch['practiceAreaCodes'] = savedPracticeAreas;
    if (Object.keys(patch).length > 0) {
      this.filterForm.patchValue(patch, { emitEvent: false });
    }
  }

  /** Restore the last-chosen export layout so the export dialog opens on it. */
  private restoreSavedExportType(): void {
    const savedExportType = this.userCookieService.getFilterValue(
      PageKey.Requests,
      'exportType',
    );
    if (savedExportType === 'flattened' || savedExportType === 'grouped') {
      this.lastExportType = savedExportType;
    }
  }

  private persistSavedFilters(): void {
    this.userCookieService.setFilterSelection(PageKey.Requests, 'statuses', [
      ...this.selectedStatuses,
    ]);
    this.userCookieService.setFilterSelection(
      PageKey.Requests,
      'requestTypes',
      this.filterForm.value.requestTypes ?? [],
    );
    this.userCookieService.setFilterSelection(
      PageKey.Requests,
      'practiceAreaCodes',
      this.filterForm.value.practiceAreaCodes ?? [],
    );
  }

  private loadLookupOptions(): void {
    // Load request statuses
    this.lookupService
      .getLookupOptions(LookupTypeCodes.REQUEST_STATUS)
      .subscribe({
        next: (options) => {
          this.availableStatuses = options;
        },
        error: (error) => {
          console.error('Error loading request statuses:', error);
        },
      });

    // Load request types
    this.lookupService
      .getLookupOptions(LookupTypeCodes.REQUEST_TYPE)
      .subscribe({
        next: (options) => {
          this.availableRequestTypes = options;
        },
        error: (error) => {
          console.error('Error loading request types:', error);
        },
      });
  }

  getPracticeAreaLabel(code: string): string {
    return (
      this.practiceAreas.find((pa) => pa.practiceCode === code)?.description ??
      code
    );
  }

  getPracticeAreaLabels(codes: string[] | null | undefined): string[] {
    return (codes ?? []).map((code) => this.getPracticeAreaLabel(code));
  }

  private loadPracticeAreas(): void {
    // Full list drives the table badge descriptions (must resolve any code on a request).
    this.practiceAreaService.getAllPracticeAreas().subscribe({
      next: (practiceAreas) => {
        this.practiceAreas = practiceAreas;
      },
      error: (error) => {
        console.error('Error loading practice areas:', error);
      },
    });

    // User's own practice areas drive the filter dropdown, matching the resource directory.
    // Fetched fresh (rather than read from the login-time cache) so an area the user was just
    // granted shows up as a filter option without a full page refresh.
    this.resourceService.getUserPracticeAreas().subscribe({
      next: (practiceAreas) => {
        this.filterPracticeAreas = practiceAreas;
      },
      error: (error) => {
        console.error('Error loading user practice areas:', error);
      },
    });
  }

  private setupSearchSubscription(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadRequests();
      });
  }

  loadRequests(isInitialLoad = false): void {
    // The popover is anchored to a row that is about to be replaced.
    this.closeAssignedPopover();

    // Cancel any pending search requests
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
    }

    // Determine if this is a search operation (user interaction) or initial load
    const isSearchOperation =
      !isInitialLoad &&
      (this.searchControl.value ||
        this.filterForm.value.requestTypes?.length > 0);

    if (isSearchOperation) {
      this.isSearching = true;
      // Don't set isLoading for search operations
    } else {
      this.isLoading = true;
      this.isSearching = false;
      // Only show skeleton data for initial loads
      if (isInitialLoad) {
        this.showSkeletonData();
      }
    }
    this.error = null;

    const requestParams = this.buildListRequest(
      this.pageIndex + 1,
      this.pageSize,
    );

    this.pendingSearchRequest = this.requestService
      .getRequests(requestParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: PagedResult<RequestDto>) => {
          this.requests = result.items;
          this.totalCount = result.totalCount;
          this.generateAssignedValues(result.items);
          this.isLoading = false;
          this.isSearching = false;
          this.pendingSearchRequest = null;
          this.loadClientMatterInfoForRequests(result.items);
        },
        error: (error) => {
          console.error('Error loading requests:', error);
          this.error = 'Failed to load requests. Please try again.';
          this.isLoading = false;
          this.isSearching = false;
          this.pendingSearchRequest = null;
        },
      });
  }

  /**
   * Builds the list query from the page's current search, filters, and sort. Shared by the
   * table load and the export so the workbook matches exactly what the user is looking at.
   */
  private buildListRequest(page: number, pageSize: number): RequestListRequest {
    // Build request params based on selected statuses
    const statuses =
      this.selectedStatuses.length > 0 ? [...this.selectedStatuses] : [];

    // Map UI column names to API field names
    const sortFieldMap: { [key: string]: string } = {
      dateSubmitted: 'CreatedAt',
      id: 'Id',
      clientMatter: 'ClientMatterNumber',
      requestorName: 'RequestorName',
      requestType: 'RequestType',
    };

    // Get sort parameters from MatSort or use preserved state
    const sortInstance = this.sort();
    const activeSort = sortInstance?.active || this.currentSortActive;
    const activeDirection =
      sortInstance?.direction || this.currentSortDirection;

    const sortBy =
      activeSort && activeDirection
        ? sortFieldMap[activeSort] || 'CreatedAt'
        : 'CreatedAt';
    const sortDirection = activeDirection;

    return {
      page,
      pageSize,
      search: this.searchControl.value || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      requestTypes:
        this.filterForm.value.requestTypes?.length > 0
          ? this.filterForm.value.requestTypes
          : undefined,
      practiceAreaCodes:
        this.filterForm.value.practiceAreaCodes?.length > 0
          ? this.filterForm.value.practiceAreaCodes
          : undefined,
      sortBy: sortBy,
      sortDirection: sortDirection,
    };
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadRequests();
  }

  onStatusFilterChange(statuses: string[]): void {
    this.selectedStatuses = statuses;
    this.pageIndex = 0;
    this.persistSavedFilters();
    this.loadRequests();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.persistSavedFilters();
    this.loadRequests();
  }

  resetFilters(): void {
    this.searchControl.setValue('');
    this.filterForm.patchValue({
      requestTypes: [],
      practiceAreaCodes: [],
    });
    this.selectedStatuses = ['New', 'Pending'];

    // Reset sorting to default (dateSubmitted descending)
    this.currentSortActive = 'dateSubmitted';
    this.currentSortDirection = 'desc';
    const sortInstance = this.sort();
    if (sortInstance) {
      sortInstance.active = 'dateSubmitted';
      sortInstance.direction = 'desc';
      sortInstance.sortChange.emit({
        active: 'dateSubmitted',
        direction: 'desc',
      });
    }

    this.pageIndex = 0;
    this.persistSavedFilters();
    this.loadRequests();
  }

  hasFiltersChanged(): boolean {
    const searchValue = this.searchControl.value?.trim();
    const requestTypes = this.filterForm.value.requestTypes;
    const practiceAreaCodes = this.filterForm.value.practiceAreaCodes;
    const defaultStatuses = ['New', 'Pending'];
    const isNotDefaultStatus =
      this.selectedStatuses.length !== defaultStatuses.length ||
      !defaultStatuses.every((s) => this.selectedStatuses.includes(s));

    // Check if sorting differs from default (dateSubmitted desc)
    const sortingChanged =
      this.currentSortActive !== 'dateSubmitted' ||
      this.currentSortDirection !== 'desc';

    return !!(
      searchValue ||
      (requestTypes && requestTypes.length > 0) ||
      (practiceAreaCodes && practiceAreaCodes.length > 0) ||
      isNotDefaultStatus ||
      sortingChanged
    );
  }

  getSelectedCountDisplay(fieldName: string): string {
    const values = this.filterForm.get(fieldName)?.value;
    if (values && values.length > 0) {
      return `(${values.length})`;
    }
    return '';
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'new':
        return 'badge-new';
      case 'pending':
        return 'badge-pending';
      case 'fulfilled':
        return 'badge-fulfilled';
      default:
        return '';
    }
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  updateTooltipDisabled(el: HTMLElement, tooltip: MatTooltip): void {
    tooltip.disabled = el.scrollWidth <= el.clientWidth;
  }

  truncateDescription(
    description: string | undefined,
    maxLength: number = 80,
  ): string {
    if (!description) return '-';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + '...';
  }

  shouldShowTooltip(
    description: string | undefined,
    maxLength: number = 80,
  ): boolean {
    return !!description && description.length > maxLength;
  }

  // Populate assigned values from DTO data
  private generateAssignedValues(requests: RequestDto[]): void {
    requests.forEach((request) => {
      const assigned = request.assignedResourcesWithType?.length || 0;
      const totalPossible = request.requestedResourceCount || 0;
      this.assignedValues.set(request.id, `${assigned}/${totalPossible}`);
    });
  }

  /**
   * Progressively load client name + matter name for visible requests.
   * The client matter ID is shown immediately; names appear as skeletons
   * and are filled in when this batch call returns.
   */
  private loadClientMatterInfoForRequests(requests: RequestDto[]): void {
    if (this.pendingClientMatterInfoRequest) {
      this.pendingClientMatterInfoRequest.unsubscribe();
      this.pendingClientMatterInfoRequest = null;
    }

    const clientMatterNumbers = Array.from(
      new Set(
        requests.flatMap((r) => {
          // Prefer the multi-matter array; fall back to the legacy scalar.
          const fromArray =
            r.clientMatterNumbers && r.clientMatterNumbers.length > 0
              ? r.clientMatterNumbers
              : [];
          const fromScalar =
            fromArray.length === 0 && r.clientMatterNumber
              ? [r.clientMatterNumber]
              : [];
          return [...fromArray, ...fromScalar];
        }),
      ),
    );

    if (clientMatterNumbers.length === 0) {
      this.loadingClientMatterDetails = false;
      return;
    }

    this.loadingClientMatterDetails = true;

    this.pendingClientMatterInfoRequest = this.matterService
      .getClientMatterInfo(clientMatterNumbers)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (infos) => {
          // Cache for the matters dialog so every matter can be shown with its names.
          for (const info of infos) {
            this.matterInfoByNumber.set(
              info.clientMatterNumber.toUpperCase(),
              info,
            );
          }
          // Fill in headings on a popover opened before these names arrived.
          this.refreshAssignedPopoverGroups();
          this.requests = this.requests.map((r) => {
            if (!r.clientMatterNumber) {
              return r;
            }
            const info = this.matterInfoByNumber.get(
              r.clientMatterNumber.toUpperCase(),
            );
            if (!info) {
              return r;
            }
            return {
              ...r,
              clientName: info.clientName ?? r.clientName,
              matterTitle: info.matterName ?? r.matterTitle,
            };
          });
          this.loadingClientMatterDetails = false;
          this.pendingClientMatterInfoRequest = null;
        },
        error: (error: unknown) => {
          console.error('Error loading client/matter info:', error);
          this.loadingClientMatterDetails = false;
          this.pendingClientMatterInfoRequest = null;
        },
      });
  }

  getAssignedValue(requestId: number): string {
    return this.assignedValues.get(requestId) || '0/0';
  }

  /**
   * Keys rows by request id so the table reuses row views. `loadClientMatterInfoForRequests`
   * re-maps `requests` into new objects once names resolve; without this the differ would
   * tear down every row, destroying the `cdkOverlayOrigin` an open Assigned popover is
   * anchored to (skeleton rows use negative ids, which are still unique).
   */
  trackByRequestId(_index: number, request: RequestDto): number {
    return request.id;
  }

  /**
   * Opens (or closes) the popover listing who is assigned to a request, grouped by matter.
   * The rows come straight off the list payload, which the API already sources from the
   * fulfillment snapshot for FULFILLED requests and from the live assignments otherwise.
   */
  toggleAssignedPopover(
    request: RequestDto,
    origin: CdkOverlayOrigin,
    event: Event,
  ): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.assignedPopoverOrigin === origin) {
      this.closeAssignedPopover();
      return;
    }

    this.assignedPopoverRequest = request;
    this.refreshAssignedPopoverGroups();
    this.assignedPopoverOrigin = origin;
  }

  /**
   * Idempotent so it is safe to call from the overlay's own `detach` output, which fires
   * when the scroll strategy closes the popover without going through the trigger.
   */
  closeAssignedPopover(): void {
    if (!this.assignedPopoverOrigin) {
      return;
    }
    this.assignedPopoverOrigin = null;
    this.assignedPopoverGroups = [];
    this.assignedPopoverRequest = null;
  }

  /**
   * Rebuilds the open popover's groups. Called on open, and again when the background
   * client/matter lookup resolves — a popover opened while those names were still loading
   * would otherwise show bare matter numbers until it was closed and reopened.
   */
  private refreshAssignedPopoverGroups(): void {
    const request = this.assignedPopoverRequest;
    if (!request) {
      return;
    }
    const matterNumbers = this.getRequestMatterNumbers(request);
    this.assignedPopoverShowHeadings = matterNumbers.length > 1;
    this.assignedPopoverGroups = this.buildAssignedGroups(
      request,
      matterNumbers,
    );
  }

  /**
   * Buckets a request's assigned resources under each of its matters. A resource on two
   * matters appears under both. Anything left over (an assignment whose matter is no longer
   * on the request) is kept in a trailing group so nobody is silently dropped.
   *
   * A resource's matter numbers and the request's come from different sources and are not
   * guaranteed to be the same string, so matching is case-insensitive and single-matter
   * requests skip matching entirely — the same shortcut request-details' `assignedForMatter`
   * takes. Without it a near-miss would render "None assigned" directly above a catch-all
   * group listing everyone.
   */
  private buildAssignedGroups(
    request: RequestDto,
    matterNumbers: string[],
  ): AssignedMatterGroup[] {
    const assignedResources = request.assignedResourcesWithType ?? [];
    const singleMatter = matterNumbers.length <= 1;
    const grouped = new Set<AssignedResourceDto>();

    const groups: AssignedMatterGroup[] = matterNumbers.map((number) => {
      const matterKey = number.toUpperCase();
      const info = this.matterInfoByNumber.get(matterKey);
      const resources = assignedResources.filter(
        (r) =>
          singleMatter ||
          (r.clientMatterNumbers ?? []).some(
            (m) => m.toUpperCase() === matterKey,
          ),
      );
      resources.forEach((r) => grouped.add(r));
      return {
        clientMatterNumber: number,
        clientName: info?.clientName,
        matterName: info?.matterName,
        resources,
      };
    });

    const ungrouped = assignedResources.filter((r) => !grouped.has(r));
    if (ungrouped.length > 0) {
      groups.push({ clientMatterNumber: null, resources: ungrouped });
    }

    return groups;
  }

  /**
   * Display name for an assigned resource. The API sends an empty `fullName` (not null) when
   * the employee can no longer be resolved — terminated, or dropped from the external
   * employee API — so these fall back with `||`, ending at the employee id rather than a
   * nameless row. Mirrors the requestor cell's `fullName || first + last || employeeId`.
   */
  assignedResourceName(resource: AssignedResourceDto): string {
    return (
      resource.fullName ||
      `${resource.firstName ?? ''} ${resource.lastName ?? ''}`.trim() ||
      resource.employeeId
    );
  }

  /**
   * Chip label for an assignment type. The API only defaults the display name when the lookup
   * name is null, not when it is empty, so fall back to the category code the chip's colour is
   * keyed off rather than hardcoding one of the two values — otherwise a COVERAGE assignment
   * with a blank name renders a secondary-coloured chip reading "Primary". Empty means no chip.
   */
  assignmentTypeLabel(resource: AssignedResourceDto): string {
    if (resource.assignmentTypeCategoryName) {
      return resource.assignmentTypeCategoryName;
    }
    const category = resource.assignmentTypeCategory;
    return category
      ? category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
      : '';
  }

  navigateToResource(employeeId: string): void {
    this.closeAssignedPopover();
    this.router.navigate(['/resources', employeeId]);
  }

  /** Resolves the full set of client-matter numbers for a request (multi-matter array, else scalar). */
  getRequestMatterNumbers(request: RequestDto): string[] {
    if (request.clientMatterNumbers && request.clientMatterNumbers.length > 0) {
      return request.clientMatterNumbers;
    }
    return request.clientMatterNumber ? [request.clientMatterNumber] : [];
  }

  /** Opens a modal listing every matter on the request, each linking to its matter detail page. */
  openMattersDialog(request: RequestDto, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const matters = this.buildAssignedGroups(
      request,
      this.getRequestMatterNumbers(request),
    )
      // Intentionally drops buildAssignedGroups' catch-all group: this dialog lists matters,
      // and a null matter number has no row to occupy or detail page to link to. Those
      // assignments are still surfaced by the Assigned popover under "Other assignments".
      .filter((group) => group.clientMatterNumber !== null)
      .map((group) => ({
        clientMatterNumber: group.clientMatterNumber!,
        clientName: group.clientName,
        matterName: group.matterName,
        resources: group.resources.map((r) => this.assignedResourceName(r)),
      }));

    this.dialog.open<MattersListDialogComponent, MattersListDialogData, void>(
      MattersListDialogComponent,
      {
        width: '720px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        autoFocus: false,
        data: { requestId: request.id, matters },
      },
    );
  }

  /** Opens the export-config dialog, seeded with the remembered layout. The dialog
   *  owns the layout choice; search, status, request type, practice area, and
   *  sort are inherited from the page so the workbook matches the list. */
  openExportConfig(): void {
    if (this.isExporting) {
      return;
    }

    const data: ExportConfigDialogData = {
      layout: this.lastExportType,
      layoutDescriptions: {
        grouped: 'One row per request with its matter assignments nested beneath',
        flattened: 'One row per assigned matter for filtering and analysis',
      },
      // Requests carry neither billing hours nor time off entries.
      showBillingPeriod: false,
      showTimeOffEntries: false,
      subjectLabel: 'requests',
      viewLabel: 'Requests',
      activeFilterCount: this.activeFilterCount(),
      // The dialog runs the export itself so it can show progress on its Export
      // button and close only once the download has completed.
      runExport: (config: ExportConfigDialogResult) =>
        this.runExportWithConfig(config),
    };

    this.dialog.open(ExportConfigDialogComponent, {
      width: '460px',
      autoFocus: false,
      disableClose: true,
      data,
    });
  }

  /** Count of active list filters, for the dialog's read-only "inherited filters" summary. */
  private activeFilterCount(): number {
    let count = 0;
    if (this.searchControl.value?.trim()) {
      count++;
    }
    if (this.filterForm.value.requestTypes?.length > 0) {
      count++;
    }
    if (this.filterForm.value.practiceAreaCodes?.length > 0) {
      count++;
    }
    if (this.selectedStatuses.length > 0) {
      count++;
    }
    return count;
  }

  /** Runs the export for the dialog's layout over the page's current filters and
   *  sort. Only the layout is remembered for next time. Returns an observable that
   *  completes once the download has been delivered (errors if it fails) so the
   *  dialog can drive its spinner and close on success. */
  private runExportWithConfig(
    config: ExportConfigDialogResult,
  ): Observable<void> {
    this.lastExportType = config.layout;
    this.userCookieService.setFilterValue(
      PageKey.Requests,
      'exportType',
      config.layout,
    );

    // Paging is a required part of the list shape but the export ignores it.
    const request = this.buildListRequest(1, this.pageSize);
    this.isExporting = true;

    return this.requestService.exportRequests(request, config.layout).pipe(
      takeUntil(this.destroy$),
      map((response) => {
        if (!response.body) {
          // Treat an empty payload as a failure so the dialog stays open and
          // the user can widen their filters and retry.
          this.snackbarService.showError('Export returned no data');
          throw new Error('EXPORT_NO_DATA');
        }
        const filename = parseFilenameFromContentDisposition(
          response.headers.get('Content-Disposition'),
          `requests_${new Date().toISOString().slice(0, 10)}.xlsx`,
        );
        downloadBlob(response.body, filename);
        this.snackbarService.showSuccess('Export complete');
      }),
      catchError((err) => {
        // The no-data branch already surfaced its own message; only a genuine
        // request failure needs the generic toast + console log.
        if (!(err instanceof Error && err.message === 'EXPORT_NO_DATA')) {
          console.error('Error exporting requests:', err);
          this.snackbarService.showError('Failed to export requests');
        }
        return throwError(() => err);
      }),
      finalize(() => {
        this.isExporting = false;
      }),
    );
  }

  /** Opens the create-request wizard inside a modal. */
  onNewRequest(): void {
    const dialogRef = this.dialog.open<
      RequestWizardDialogComponent,
      { mode: 'create' | 'edit'; requestId?: number },
      boolean
    >(RequestWizardDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'new-request-dialog-panel',
      autoFocus: false,
      disableClose: true,
      data: { mode: 'create' },
    });

    dialogRef.afterClosed().subscribe((success) => {
      if (success) this.loadRequests();
    });
  }

  onRequestClick(request: RequestDto): void {
    if (request.id < 0) {
      return;
    }
    this.router.navigate(['/requests', request.id]);
  }

  private showSkeletonData(): void {
    const skeletonData = this.createSkeletonData(this.pageSize);
    this.requests = skeletonData;
  }

  private createSkeletonData(count: number): RequestDto[] {
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        id: -(index + 1),
        dateSubmitted: new Date(),
        requestType: undefined,
        status: undefined,
        description: '',
        requestedResourceCount: undefined,
        startDate: undefined,
        endDate: undefined,
        preferredEmployeeIds: undefined,
        clientMatterNumber: undefined,
        preferredEmployees: undefined,
        createdAt: new Date(),
        updatedAt: undefined,
        isActive: true,
      }));
  }
}
